// ---------------------------------------------------------------------------
// Bar charts, drawn out of divs.
//
// There is no SVG anywhere in this app and this is not the place to introduce
// the first. The house style is `PieClock` — stacked divs and a conic-gradient —
// and the two bars that already exist are a track div with a fill div at a
// percentage width. A chart library for six bars would be a dependency to draw
// six rectangles.
//
// **The markup is a table, and the bars are decoration on top of it.** A
// `role="img"` with an `aria-label` is right for a disc and wrong for a series
// of labelled numbers: a screen reader gets a real table with a caption, row
// headers and cells, and the bars are `aria-hidden` divs inside those cells. The
// chart degrades to the data rather than to a sentence describing the data.
//
// Two things are easy to get wrong and are handled here:
//
//   A bar at `width: 0%` renders identically to no data at all. A nonzero count
//   gets a minimum visible stub, and the number is always in text beside it.
//
//   Nesting washes composites deeper than either alone (CLAUDE.md, and two of
//   the original contrast failures got in that way). The track is an OPAQUE fill
//   rather than a tint, so a bar inside a tinted card is still one wash deep.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';

/**
 * A bar's fill. Every one clears 3:1 against the track and against every
 * surface, and `palette.test.ts` holds them there.
 *
 * `blue-400` rather than `blue-500`, which that test rejected: `blue-500` is
 * 2.76:1 on the track in the dark theme. It looks fine and is not readable.
 */
export type BarTone = 'blue' | 'accent' | 'good' | 'alert';

const BAR_FILL: Record<BarTone, string> = {
  blue: 'bg-blue-400',
  accent: 'bg-accent',
  good: 'bg-good',
  alert: 'bg-alert',
};

/** Below this a bar is a hairline, which reads as absent rather than as small. */
const MIN_VISIBLE_PERCENT = 3;

export interface Bar {
  /** The row header. Short: it is a table cell, not a legend. */
  label: string;
  /** What the header is called to anything reading the table out. */
  longLabel?: string;
  value: number;
  /** What to print in the value cell. Defaults to the value itself. */
  display?: string;
  tone?: BarTone;
}

export function Histogram({
  caption,
  bars,
  max,
  emptyNote = 'Nothing here yet.',
}: {
  caption: string;
  bars: Bar[];
  /** The scale's top. Defaults to the largest bar, which is usually right. */
  max?: number;
  emptyNote?: string;
}): ReactElement {
  const ceiling = max ?? bars.reduce((highest, bar) => Math.max(highest, bar.value), 0);
  const anything = bars.some((bar) => bar.value > 0);

  if (!anything) {
    return (
      <div className="flex flex-col gap-2">
        <p className="label-mono">{caption}</p>
        <p className="text-sm text-ink-dim">{emptyNote}</p>
      </div>
    );
  }

  return (
    <table className="w-full border-collapse" data-chart="histogram">
      <caption className="label-mono mb-2 text-left">{caption}</caption>
      <tbody>
        {bars.map((bar) => {
          const share = ceiling === 0 ? 0 : (bar.value / ceiling) * 100;
          const width = bar.value === 0 ? 0 : Math.max(share, MIN_VISIBLE_PERCENT);
          return (
            <tr key={bar.label} data-bucket={bar.label} data-count={bar.value}>
              <th
                scope="row"
                className="w-14 py-1 pr-3 text-left font-mono text-[12px] font-semibold text-ink-muted"
              >
                <span aria-hidden="true">{bar.label}</span>
                <span className="sr-only">{bar.longLabel ?? bar.label}</span>
              </th>
              <td className="py-1">
                {/* Opaque track. A translucent one inside a tinted card is the
                    two-wash composite the palette was never solved for. */}
                <div aria-hidden="true" className="h-3 w-full rounded-full bg-raised">
                  <div
                    className={`h-full rounded-full transition-[width] duration-300 ${
                      BAR_FILL[bar.tone ?? 'blue']
                    }`}
                    style={{ width: `${width}%` }}
                  />
                </div>
              </td>
              <td className="tabular w-12 py-1 pl-3 text-right font-mono text-[13px] font-semibold">
                {bar.display ?? bar.value}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
